import './AccessAudit.scss';

import { AccessAuditExportButton } from './AccessAuditExportButton';
import { AccessLoadingSpinner } from './AccessLoadingSpinner';
import { CompareActions } from './AccessAuditCompareButton';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { DataAccessSearch } from './DataAccessSearch';
import { getColumns } from './AccessAuditColumns';
import { getDataAccessRoute } from '@lib/routes';
import { parseQueryString } from '@sonar-software/sonar-ts-lib';
import { SelectionTable } from '@ui/tables';
import { SonarCard } from '@ui/general';
import { SonarText } from '@ui/text';
import { sort } from './@arch/lib';
import { SortByFilterQueryParams } from '@periscopes/general/filters';

import { AccessAuditDetails, coreActions, ObjectPermission, SchemaFormat } from '@sonar-software/sonar-core';
import {
	AccessComparisonObjectQueryParams,
	useAction,
	useQueryParams,
	useSelectionTableWithInversion,
	useSonarSelector,
	useTableFiltering
} from '@hooks';
import { Button, Row, Space } from 'antd';
import {
	DynamicFilter,
	DynamicQueryParams,
	SonarV3SortDirection,
	SortByOption
} from '@sonar-software/sonar-ts-types';
import { Else, If, Then } from 'react-if';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TableFilter, TableModifier } from '@ui/table-modifier';
import {
	translateFilterAndOrderingToQueryParams,
	translateFilterQueryParamsToDynamicQueryLanguage,
	translateOrderQueryParamsToDynamicQueryLanguage
} from '@ui/table-modifier/@arch/lib';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export type AccessAuditQueryParams = DynamicQueryParams & SortByFilterQueryParams & {
	objectName?: string;
};

const ACCESS_NAME = 'AccessName';
const ACCESS_TYPE = 'AccessType';
const ACCESS_OBJECT_SOURCE = 'AccessObjectSource';

const filters: Array<TableFilter> = [
	{
		filterFormat: SchemaFormat.Text,
		filterGroup: COPY_SPECIFIC.DATA_ACCESS,
		filterId: ACCESS_NAME,
		filterLabel: COPY_SPECIFIC.ACCESS_NAME
	}, {
		filterFormat: SchemaFormat.Enum,
		filterGroup: COPY_SPECIFIC.DATA_ACCESS,
		filterId: ACCESS_TYPE,
		filterLabel: COPY_SPECIFIC.ACCESS_TYPE
	}, {
		filterFormat: SchemaFormat.Enum,
		filterGroup: COPY_SPECIFIC.DATA_ACCESS,
		filterId: ACCESS_OBJECT_SOURCE,
		filterLabel: COPY_SPECIFIC.ACCESS_OBJECT_SOURCE
	}, {
		filterFormat: SchemaFormat.Enum,
		filterGroup: COPY_SPECIFIC.DATA_ACCESS,
		filterId: 'AccessLevel',
		filterLabel: COPY_SPECIFIC.OBJECT_ACCESS
	}, {
		filterFormat: SchemaFormat.Checkmark,
		filterGroup: COPY_SPECIFIC.DATA_ACCESS,
		filterId: 'UserIsActive',
		filterLabel: COPY_SPECIFIC.USER_STATUS,
		filterMetadata: COPY_SPECIFIC.ACTIVE
	}, {
		filterFormat: SchemaFormat.PermissionSources,
		filterGroup: COPY_SPECIFIC.DATA_ACCESS,
		filterId: 'PermissionSourceUsers',
		filterLabel: COPY_SPECIFIC.USERS_IN_PERMISSION_SOURCE
	}
];

const sortOptions: Array<SortByOption> = [
	{
		direction: SonarV3SortDirection.Ascending,
		key: ACCESS_NAME,
		label: COPY_SPECIFIC.ACCESS_NAME
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: ACCESS_OBJECT_SOURCE,
		label: COPY_SPECIFIC.ACCESS_OBJECT_SOURCE
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: ACCESS_TYPE,
		label: COPY_SPECIFIC.ACCESS_TYPE
	}
];

export const objectPermissionFullLabels: Record<ObjectPermission, string> = {
	[ ObjectPermission.create ]: COPY_SPECIFIC.CREATE,
	[ ObjectPermission.read ]: COPY_SPECIFIC.READ,
	[ ObjectPermission.edit ]: COPY_SPECIFIC.EDIT,
	[ ObjectPermission.delete ]: COPY_SPECIFIC.DELETE,
	[ ObjectPermission.viewAllData ]: COPY_SPECIFIC.VIEW_ALL_DATA,
	[ ObjectPermission.modifyAllData ]: COPY_SPECIFIC.MODIFY_ALL_DATA
};

const columns = getColumns();
const getRowKey = (accessObject: AccessAuditDetails) => accessObject.accessId;
const convertToDql = (params: AccessAuditQueryParams) => {
	const { objectName, sortBy, sortDirection, ...rest } = params;

	const selectedSortOption =
		sortOptions.find((option) => option.key === (sortBy ?? ACCESS_NAME)) || sortOptions[ 0 ];

	return {
		filters: translateFilterQueryParamsToDynamicQueryLanguage(rest as DynamicFilter),
		ordering: translateOrderQueryParamsToDynamicQueryLanguage({
			sortBy: selectedSortOption.key,
			sortDirection: sortDirection ?? selectedSortOption.direction
		})
	};
};

export const accessAuditDataSelectors =
	createAsyncSelectors((state) => state.core.dataSecurity.dataAccess.accessAudit.byId.get);

const onExpandedRowRender =
	(accessObject: AccessAuditDetails) =>
		<Row className='access-audit-expanded-row'>
			{ Object.keys(accessObject.permissions).map((permission: ObjectPermission) =>
				<Space
					direction='vertical'
					key={ permission }
					size={ 0 }>
					<SonarText
						className='h3'> { objectPermissionFullLabels[ permission ] }
					</SonarText>
					{ accessObject.permissions[ permission ]?.split(',').map((permissionValue: string) =>
						<div
							className='text-14'
							key={ permissionValue }>{ permissionValue }
						</div>) }
				</Space>) }
		</Row>;

export const AccessAudit: React.FC = () => {
	const [ sorting, setSorting ] = useState<boolean>(false);
	const { objectId } = useParams<{objectId: string}>();
	const lastUsedParams = useRef<AccessAuditQueryParams>();

	const navigate = useNavigate();
	const location = useLocation();

	const getAudit = useAction(coreActions.requestGetCoreDataSecurityAccessAuditById);
	const loadPermissionSources = useAction(coreActions.requestGetCoreDataSecurityDataAccessPermissionSources);
	const data = useSonarSelector(accessAuditDataSelectors.data);
	const isRequested = useSonarSelector(accessAuditDataSelectors.isRequested);
	const [ isRefreshing, setIsRefreshing ] = useState(false);

	useEffect(() => {
		if (isRefreshing && !isRequested) {
			setIsRefreshing(false);
		}
	}, [ isRefreshing, isRequested ]);

	const parsedQuery = useMemo(
		() => parseQueryString(location.search) as AccessAuditQueryParams,
		[ location.search ]
	);

	const initialParams = useMemo(
		() => ({
			filters: 'UserIsActive~eq~"true"',
			ordering: '',
			...parsedQuery
		}),
		[ parsedQuery ]
	);

	const updateUrl = useCallback(
		(queryObject: AccessAuditQueryParams) => {
			navigate(
				getDataAccessRoute(queryObject),
				{ replace: true }
			);
		}, [ navigate ]
	);

	const callApi = useCallback((params?: AccessAuditQueryParams) => {
		if (objectId) {
			lastUsedParams.current = params;

			getAudit({
				filters: params?.filters ?? '',
				salesforceId: objectId
			});
		}
	}, [ getAudit, objectId ]);

	const { queryParams, updateParams: originalUpdateParams } = useQueryParams<AccessAuditQueryParams>({
		defaultParams: initialParams,
		onUpdate: (_, __, combined) => {
			const urlParams: AccessAuditQueryParams = {
				filters: combined.filters ?? '',
				objectName: combined.objectName,
				ordering: combined.ordering ?? ''
			};

			updateUrl(urlParams);
		}
	});

	const { isFiltering, updateParams } = useTableFiltering(originalUpdateParams, isRequested);

	useEffect(() => {
		if (parsedQuery.objectName !== queryParams.objectName) {
			updateParams({ objectName: parsedQuery.objectName });
		}
	}, [ parsedQuery, queryParams, updateParams ]);

	const sortedData = useMemo(() => {
		if (!data) {
			return [];
		}

		setSorting(true);

		const params = translateFilterAndOrderingToQueryParams(
			filters, sortOptions, queryParams.filters, queryParams.ordering
		);

		const sorted = [ ...data ].sort((a, b) => {
			if (params.sortBy === ACCESS_TYPE) {
				return sort(params.sortDirection, a.accessType, b.accessType, a.accessName, b.accessName);
			}

			if (params.sortBy === ACCESS_OBJECT_SOURCE) {
				return sort(
					params.sortDirection, a.accessObjectSource, b.accessObjectSource, a.accessName, b.accessName
				);
			}

			return sort(params.sortDirection, a.accessName, b.accessName);

		});

		setSorting(false);

		return sorted;

	}, [ data, queryParams, setSorting ]);

	useEffect(() => {
		const lastUsed = lastUsedParams.current;

		if (queryParams.filters === lastUsed?.filters &&
			queryParams.objectName === lastUsed?.objectName) {
			return;
		}

		callApi(queryParams);
	}, [ callApi, queryParams ]);

	const selectionData = useSelectionTableWithInversion<AccessAuditDetails, string>({
		getRowKey,
		results: sortedData,
		totalCount: sortedData.length
	});

	const handleCompare = useCallback(() => {
		const params: AccessComparisonObjectQueryParams = {
			accessId: [],
			accessType: [],
			objectName: objectId
		};

		selectionData.selectedKeys.forEach((key) => {
			const access = sortedData.find((item) => item.accessId === key);
			if (!access) {
				return;
			}
			params.accessType?.push(access.accessType);
			params.accessId?.push(access.accessId);
		});

		navigate(getDataAccessRoute(params));

	}, [ objectId, navigate, selectionData, sortedData ]);


	// Conditionally returns the CompareActions component only if selections exist.
	// This must be done at this level (not inside CompareActions) because TableModifier
	// relies on `Boolean(actionSection)` to toggle the 'with-compare' CSS class.
	// If we always return a component—even if CompareActions renders null internally—
	// the class would remain applied, breaking transitions.
	const actionSection = useMemo(() => {
		const hasSelections = selectionData.selectedKeys.length > 0;

		if (!hasSelections) {
			return null;
		}

		return (
			<CompareActions
				onClearSelection={ selectionData.resetSelection }
				onCompare={ handleCompare }
				selectedCount={ selectionData.selectedKeys.length }
				totalCount={ sortedData.length } />
		);
	}, [ sortedData.length, handleCompare, selectionData ]);

	// Determine when to show skeleton vs table with transparent overlay
	const showSkeleton = (isRequested && (!data || !isRefreshing)) && !isFiltering.current;

	const showTransparentOverlay =
		(isRequested && Boolean(data) && (isRefreshing || isFiltering.current)) ||
		sorting;

	return (
		<SonarCard title={ COPY_SPECIFIC.ACCESS_AUDIT }>
			<DataAccessSearch defaultValue={ initialParams.objectName } />
			<If condition={ showSkeleton }>
				<Then>
					<AccessLoadingSpinner
						loading={ isRequested }
						sorting={ sorting } />
				</Then>
				<Else>
					<TableModifier
						actionSection={ actionSection }
						className='compact-action-bar'
						convertToDynamicQueryLanguage={ (params: AccessAuditQueryParams) => convertToDql(params) }
						deferFiltering
						extra={
							<>
								<Button
									loading={ isRefreshing }
									onClick={ () => {
										setIsRefreshing(true);
										callApi(queryParams);
										loadPermissionSources();
									} }>
									{ COPY_SPECIFIC.REFRESH_DATA }
								</Button>
								<AccessAuditExportButton
									queryParams={ {
										...queryParams,
										salesforceId: objectId ?? ''
									} } />
							</>
						}
						filterDisplayType='menu'
						filters={ filters }
						filtersString={ queryParams.filters }
						onChange={ updateParams }
						orderString={ queryParams.ordering }
						sortOptions={ sortOptions } />
					<AccessLoadingSpinner
						loading={ showTransparentOverlay }
						sorting={ sorting }>
						<SelectionTable
							className='access-audit-table'
							columns={ columns }
							dataSource={ sortedData }
							expandable={ { expandedRowRender: onExpandedRowRender } }
							hideSelectAll
							hideSelection
							pagination={ { defaultPageSize: 50 } }
							rowKey={ getRowKey }
							selectionData={ selectionData } />
					</AccessLoadingSpinner>
				</Else>
			</If>
		</SonarCard>
	);
};
